import moment from 'moment';

import type { ModelSupportChat } from '@app/api/Support';

const isSupportChat = (value: unknown): value is ModelSupportChat =>
  Boolean(value) &&
  typeof value === 'object' &&
  Array.isArray((value as ModelSupportChat).Messages);

/**
 * Разбирает payload WS-события OnSupportMessage. Бэкенд присылает ту же модель,
 * что и GetSupportMessages (с одним сообщением), по конвенции хаба — строкой
 * в конверте `{ jsonData }`. Поддерживаем и голую строку/объект на случай
 * другой сериализации.
 */
export const parseSupportMessageEvent = (
  raw: unknown
): ModelSupportChat | null => {
  let payload: unknown = raw;

  if (payload && typeof payload === 'object' && 'jsonData' in payload) {
    payload = (payload as { jsonData: unknown }).jsonData;
  }

  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }

  return isSupportChat(payload) ? payload : null;
};

/**
 * Вливает пришедшие по WS сообщения в закешированную переписку (мутирует
 * draft из updateQueryData): новое сообщение встаёт на место по Timestamp,
 * уже известное (например, своё — оно приходит и после отправки) заменяется.
 */
export const mergeSupportMessageEvent = (
  draft: ModelSupportChat,
  incoming: ModelSupportChat
) => {
  const messages = (draft.Messages ??= []);
  const participants = (draft.Participants ??= []);

  incoming.Messages?.forEach((message) => {
    const existingIndex = messages.findIndex(({ Id }) => Id === message.Id);

    if (existingIndex !== -1) {
      messages[existingIndex] = message;

      return;
    }

    const insertIndex = messages.findIndex(({ Timestamp }) =>
      moment(Timestamp).isAfter(message.Timestamp)
    );

    if (insertIndex === -1) {
      messages.push(message);
    } else {
      messages.splice(insertIndex, 0, message);
    }

    draft.TotalMessages += 1;
  });

  // Участников не склеиваем: при повторе ParticipantId берётся первая
  // запись (см. mapSupportMessages), поэтому добавляем только новых.
  incoming.Participants?.forEach((participant) => {
    const isKnown = participants.some(
      ({ ParticipantId }) => ParticipantId === participant.ParticipantId
    );

    if (!isKnown) {
      participants.push(participant);
    }
  });
};
